import React, { useState } from 'react';

type NativeInputProps = Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange' | 'type'
>;

export interface NumberInputProps extends NativeInputProps {
    /** Current numeric value from the owning state. */
    value: number | null | undefined;
    /**
     * Called with the parsed number on every keystroke. An empty/unparsable
     * field yields `emptyValue`. Owners may store the number as-is, or clamp
     * or reject it; whatever they store is shown once the field loses focus.
     */
    onChange: (value: number) => void;
    /** Number to report when the field is empty or unparsable. Defaults to 0. */
    emptyValue?: number;
    /** Integer mode: decimals are truncated and the text is normalised as you type. */
    integer?: boolean;
    /** Bootstrap validation styling shortcuts. */
    isInvalid?: boolean;
    isValid?: boolean;
}

const toText = (value: number | null | undefined): string =>
    value === null || value === undefined || Number.isNaN(value) ? '' : String(value);

const toNumber = (bound: string | number | undefined): number | undefined => {
    if (bound === undefined || bound === '') return undefined;
    const n = Number(bound);
    return Number.isNaN(n) ? undefined : n;
};

/**
 * Controlled `<input type="number">` backed by a numeric value that still lets
 * the user clear the field completely while editing.
 *
 * While the field has focus it shows the raw text the user typed, so deleting
 * the last digit shows an empty field instead of snapping back to `emptyValue`
 * (the classic "0 reappears" problem). The owner always receives a number: an
 * empty field reports `emptyValue`. When the field is not focused it simply
 * shows `value`, so external updates are reflected and a cleared field shows
 * what was actually stored after blur.
 *
 * Text outside `min`/`max` gets Bootstrap's `is-invalid` styling while
 * editing, so an owner that clamps or rejects the value gives visible feedback.
 */
const NumberInput: React.FC<NumberInputProps> = ({
    value,
    onChange,
    emptyValue = 0,
    integer = false,
    isInvalid,
    isValid,
    className,
    onFocus,
    onBlur,
    min,
    max,
    ...rest
}) => {
    const [focused, setFocused] = useState(false);
    const [text, setText] = useState<string>('');

    const displayed = focused ? text : toText(value);

    const parse = (raw: string): number => {
        const parsed = parseFloat(raw);
        if (Number.isNaN(parsed)) return emptyValue;
        return integer ? Math.trunc(parsed) : parsed;
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setText(toText(value));
        setFocused(true);
        onFocus?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const next = parse(raw);
        // In integer mode normalise what the user sees ("7.9" -> "7") so the
        // text never disagrees with the stored number. Empty stays empty.
        const shouldNormalise = integer && raw !== '' && !Number.isNaN(parseFloat(raw));
        setText(shouldNormalise ? String(next) : raw);
        if (!focused) setFocused(true);
        onChange(next);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setFocused(false);
        onBlur?.(e);
    };

    const minNum = toNumber(min);
    const maxNum = toNumber(max);
    const typed = focused && displayed !== '' ? parseFloat(displayed) : NaN;
    const outOfRange =
        !Number.isNaN(typed) &&
        ((minNum !== undefined && typed < minNum) || (maxNum !== undefined && typed > maxNum));

    const classes = [
        className,
        isInvalid || outOfRange ? 'is-invalid' : '',
        isValid && !outOfRange ? 'is-valid' : ''
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <input
            {...rest}
            type="number"
            min={min}
            max={max}
            className={classes || undefined}
            value={displayed}
            onFocus={handleFocus}
            onChange={handleChange}
            onBlur={handleBlur}
        />
    );
};

export default NumberInput;
